import { eventBus } from '../core/EventBus';
import { Plant } from '../plants/Plant';

export interface BalanceHistoryEntry {
  gameTime: number;
  balance: number;
}

export class CurrencyManager {
  static readonly STARTING_BALANCE = 200;
  static readonly WATER_COST = 3;
  static readonly LIGHT_COST_RATE = 0.0002;

  balance: number;
  private balanceHistory: BalanceHistoryEntry[] = [];
  private lastRecordedGameMinute: number = -1;

  constructor(balance: number = CurrencyManager.STARTING_BALANCE) {
    this.balance = balance;
  }

  canAfford(amount: number): boolean {
    return this.balance >= amount;
  }

  spend(amount: number): boolean {
    if (!this.canAfford(amount)) return false;
    this.balance -= amount;
    eventBus.emit('currency:changed', this.balance);
    return true;
  }

  earn(amount: number): void {
    this.balance += amount;
    eventBus.emit('currency:changed', this.balance);
  }

  getLightCost(intensity: number, deltaGameMinutes: number): number {
    return intensity * deltaGameMinutes * CurrencyManager.LIGHT_COST_RATE;
  }

  getPlantSellPrice(plant: Plant): number {
    const baseValue = plant.species.speciesBaseValue ?? 10;
    const stageMultipliers = [1, 2, 4, 6, 10];
    const stageIdx = Math.min(plant.currentStageIndex, stageMultipliers.length - 1);
    const currentMult = stageMultipliers[stageIdx];
    const nextIdx = Math.min(stageIdx + 1, stageMultipliers.length - 1);
    const nextMult = stageMultipliers[nextIdx];
    const stageMult = currentMult + (nextMult - currentMult) * plant.stageProgress;
    const base = baseValue * stageMult;
    return Math.floor(base * (plant.health / 100));
  }

  recordBalance(gameTime: number): void {
    const minute = Math.floor(gameTime);
    if (minute > this.lastRecordedGameMinute) {
      this.balanceHistory.push({ gameTime: minute, balance: this.balance });
      this.lastRecordedGameMinute = minute;
      if (this.balanceHistory.length > 1440) {
        this.balanceHistory.shift();
      }
    }
  }

  clearHistory(): void {
    this.balanceHistory = [];
    this.lastRecordedGameMinute = -1;
  }

  getBalanceHistory(): BalanceHistoryEntry[] {
    return [...this.balanceHistory];
  }
}
